import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Badge, Card } from "react-bootstrap";
import { FaEye, FaPrint, FaDownload, FaSearch, FaFileInvoice, FaRupeeSign, FaFilter } from "react-icons/fa";
import { Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import "./CustomerList.css";
import * as XLSX from 'xlsx';
import api from "../services/api";

const PurchaseGSTReport = () => {
  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    transactionType: "all",
    name: ""
  });


  // Sample purchase GST data
  const [purchaseTransactions, setPurchaseTransactions] = useState([]);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      // setLoading(true);
      // setError(null);
      /* */
      const response = await api.post("/purchase_list.php", {});

      if (response.data.head.code === 200) {
        // Transform the data
        const transformedData = response.data.body.map(purchase => ({
          id: purchase.id,
          // purchase_code: purchase.purchase_code,
          date: purchase.date,
          supplier_name: purchase.supplier_name,
          bill_no: purchase.bill_no,
          payment_method: purchase.payment_method,
          item_count: purchase.item_count || 1,
          sub_total: parseFloat(purchase.sub_total),
          gst_amount: parseFloat(purchase.gst_amount),
          grand_total: parseFloat(purchase.grand_total),
          amount: `₹${parseFloat(purchase.grand_total).toLocaleString('en-IN')}`
        }));

        setPurchaseTransactions(transformedData);
      } else if (response.data.head.code === 606) {
        // No purchases found - set empty array
        setPurchaseTransactions([]);
      } else {
        // setError(response.data.head.msg || "Failed to fetch purchases");
      }

    } catch (err) {
      console.error("Error fetching purchases:", err);
      // setError("Failed to load purchases. Please try again.");
    } finally {
      // setLoading(false);
    }
  };

  // Get unique suppliers for filter
  const suppliers = [...new Set(purchaseTransactions.map(transaction => transaction.supplierName))];

  // Date range options
  const dateRanges = [
    { value: "", label: "All Dates" },
    { value: "today", label: "Today" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "quarter", label: "This Quarter" }
  ];

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Helper function for date range filtering
  const filterByDateRange = (transactionDate, range) => {
    const today = new Date();
    const transDate = new Date(transactionDate);

    switch (range) {
      case "today":
        return transDate.toDateString() === today.toDateString();
      case "week":
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        return transDate >= weekAgo;
      case "month":
        return transDate.getMonth() === today.getMonth() && transDate.getFullYear() === today.getFullYear();
      case "quarter":
        const currentQuarter = Math.floor(today.getMonth() / 3) + 1;
        const transQuarter = Math.floor(transDate.getMonth() / 3) + 1;
        return transQuarter === currentQuarter && transDate.getFullYear() === today.getFullYear();
      default:
        return true;
    }
  };

  // Filter purchase transactions based on search, supplier, and date range
  const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

const parseYYYYMMDD = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

const filteredTransactions = purchaseTransactions.filter((transaction) => {
  // 🔍 Search filter (Product / Supplier / Bill No)
  const matchesSearch =
    !search ||
    transaction.supplier_name?.toLowerCase().includes(search.toLowerCase()) ||
    transaction.bill_no?.toLowerCase().includes(search.toLowerCase());

  // 📅 Date filter
  const transactionDate = parseDDMMYYYY(transaction.date);
  const from = filters.startDate ? parseYYYYMMDD(filters.startDate) : null;
  const to = filters.endDate ? parseYYYYMMDD(filters.endDate) : null;

  if (to) to.setHours(23, 59, 59, 999);

  const matchesDate =
    (!from || transactionDate >= from) &&
    (!to || transactionDate <= to);

  return matchesSearch && matchesDate;
});


  // Calculate totals
  const calculateTotals = () => {
    return filteredTransactions.reduce((totals, transaction) => {
      return {
        totalTaxableValue: totals.totalTaxableValue + transaction.taxableValue,
        totalCGST: totals.totalCGST + transaction.cgstAmount,
        totalSGST: totals.totalSGST + transaction.sgstAmount,
        totalIGST: totals.totalIGST + transaction.igstAmount,
        totalGST: totals.totalGST + transaction.totalGST,
        grandTotal: totals.grandTotal + transaction.totalAmount
      };
    }, {
      totalTaxableValue: 0,
      totalCGST: 0,
      totalSGST: 0,
      totalIGST: 0,
      totalGST: 0,
      grandTotal: 0
    });
  };

  // const totals = calculateTotals();

  // Status class functions matching your other reports
  const getPaymentStatusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "paid") return "status-badge status-approved";
    if (s === "pending") return "status-badge status-pending";
    return "status-badge status-default";
  };

  const totals = filteredTransactions.reduce(
    (acc, item) => {
      acc.gst_amount += Number(item.gst_amount) || 0;
      acc.grand_total += Number(item.grand_total) || 0;
      return acc;
    },
    { gst_amount: 0, grand_total: 0 }
  );

  const formatAmount = (value) => Number(value);

  // const getPurchaseTypeClass = (type) => {
  //   const t = String(type || "").toLowerCase();
  //   if (t === "interstate") return "status-badge status-progress";
  //   return "status-badge status-default";
  // };

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentTransactions,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredTransactions, 10);

  const handlePrint = () => {
    window.open(`/sds_polytechnic/purchasegstprint/all`, '_blank');
  };
  /*
    // Enhanced Excel Export Function
    const handleExport = () => {
      // Prepare data for Excel
      const excelData = [
        // Header
        ['PURCHASE GST REPORT', '', '', '', '', '', '', '', '', '', '', '', ''],
        ['Company: SDS POLYTECHNIC', '', '', '', '', '', '', '', '', '', '', '', ''],
        ['Report Date:', new Date().toLocaleDateString('en-IN'), '', '', '', 'Generated On:', new Date().toLocaleDateString('en-IN'), '', '', '', '', '', ''],
        ['', '', '', '', '', '', '', '', '', '', '', '', ''],
        
        // Summary Row
        ['SUMMARY', '', '', '', '', '', '', '', '', '', '', '', ''],
        ['Total Invoices:', filteredTransactions.length, 'Grand Total:', `₹${totals.grandTotal}`, 'Total GST:', `₹${totals.totalGST}`, 'Taxable Value:', `₹${totals.totalTaxableValue}`, 'Total Suppliers:', suppliers.length, 'Pending Payments:', purchaseTransactions.filter(t => t.paymentStatus === "Pending").length],
        ['', '', '', '', '', '', '', '', '', '', '', '', ''],
        
        // Column Headers
        ['S.No', 'Purchase Date', 'Invoice Number', 'Supplier Name', 'Supplier GSTIN', 'Taxable Value (₹)', 'CGST (₹)', 'SGST (₹)', 'IGST (₹)', 'Total GST (₹)', 'Total Amount (₹)', 'Payment Status'],
        
        // Purchase Transactions
        ...filteredTransactions.map((transaction, index) => [
          index + 1,
          transaction.purchaseDate,
          transaction.invoiceNumber,
          transaction.supplierName,
          transaction.supplierGSTIN,
          transaction.taxableValue,
          transaction.cgstAmount,
          transaction.sgstAmount,
          transaction.igstAmount,
          transaction.totalGST,
          transaction.totalAmount,
          // transaction.purchaseType,
          transaction.paymentStatus
        ]),
        
        // Empty row
        ['', '', '', '', '', '', '', '', '', '', '', '', ''],
        
        // Totals
        ['', 'TOTALS', '', '', '', totals.totalTaxableValue, totals.totalCGST, totals.totalSGST, totals.totalIGST, totals.totalGST, totals.grandTotal, '', ''],
        
        // Empty row
        ['', '', '', '', '', '', '', '', '', '', '', '', ''],
        
        // GST Summary
        ['GST SUMMARY', '', '', '', '', '', '', '', '', '', '', '', ''],
        ['CGST Total:', `₹${totals.totalCGST}`, '', '', '', '', '', '', '', '', '', '', ''],
        ['SGST Total:', `₹${totals.totalSGST}`, '', '', '', '', '', '', '', '', '', '', ''],
        ['IGST Total:', `₹${totals.totalIGST}`, '', '', '', '', '', '', '', '', '', '', ''],
        ['Total GST:', `₹${totals.totalGST}`, '', '', '', '', '', '', '', '', '', '', ''],
      ];
  
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);
  
      // Style the worksheet
      if (!ws['!merges']) ws['!merges'] = [];
      ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 12 } }); // Main title
      ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: 12 } }); // Company name
      ws['!merges'].push({ s: { r: 4, c: 0 }, e: { r: 4, c: 12 } }); // Summary header
  
      // Set column widths
      ws['!cols'] = [
        { wch: 5 },   // S.No
        { wch: 12 },  // Purchase Date
        { wch: 15 },  // Invoice Number
        { wch: 20 },  // Supplier Name
        { wch: 18 },  // Supplier GSTIN
        { wch: 15 },  // Taxable Value
        { wch: 12 },  // CGST
        { wch: 12 },  // SGST
        { wch: 12 },  // IGST
        { wch: 15 },  // Total GST
        { wch: 15 },  // Total Amount
        // { wch: 12 },  // Purchase Type
        { wch: 12 }   // Payment Status
      ];
  
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Purchase GST Report");
  
      // Generate and download Excel file
      XLSX.writeFile(wb, `PurchaseGSTReport_${new Date().toISOString().split('T')[0]}.xlsx`);
    };
  */
  const clearDateFilter = () => {
    setFromDate("");
    setToDate("");
  };

  const clearAllFilters = () => {
    setSearch("");
    setSupplierFilter("");
    setDateRangeFilter("");
    setFromDate("");
    setToDate("");
  };

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Purchase GST Report</h2>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col xs={6} md={4}>
          <Card className="stat-card text-center border-0 bg-primary text-white">
            <Card.Body>
              <h4 className="mb-1">{filteredTransactions.length}</h4>
              <small>Total Purchase</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={4}>
          <Card className="stat-card text-center border-0 bg-success text-white">
            <Card.Body>
              <h4 className="mb-1">₹{totals.grand_total}</h4>
              <small>Total Amount</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={4}>
          <Card className="stat-card text-center border-0 bg-info text-white">
            <Card.Body>
              <h4 className="mb-1">₹{totals.gst_amount}</h4>
              <small>Total GST</small>
            </Card.Body>
          </Card>
        </Col>
        {/* <Col xs={6} md={2}>
          <Card className="stat-card text-center border-0 bg-warning text-dark">
            <Card.Body>
              <h4 className="mb-1">₹{totals.totalTaxableValue}</h4>
              <small>Taxable Value</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card className="stat-card text-center border-0 bg-secondary text-white">
            <Card.Body>
              <h4 className="mb-1">
                {suppliers.length}
              </h4>
              <small>Suppliers</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card className="stat-card text-center border-0 bg-dark text-white">
            <Card.Body>
              <h4 className="mb-1">
                {purchaseTransactions.filter(t => t.paymentStatus === "Pending").length}
              </h4>
              <small>Pending Payments</small>
            </Card.Body>
          </Card>
        </Col> */}
      </Row>


      {/* Filters Section */}
      <div className="filter-section mb-3 p-3 bg-light rounded shadow-sm">
        <Row className="align-items-center mb-2">
          <Col>
            <h5 className="mb-0">
              <FaFilter className="me-2" />
              Filters
            </h5>
          </Col>
        </Row>

        <Row className="g-2">
          <Col xs={12} md={3} lg={3}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>

          <Col xs={12} md={3} lg={3}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={6} className="mb-2 mb-md-0">
            <div className="position-relative">
              <Form.Label>Search</Form.Label>
              <Form.Control
                placeholder="Search by Supplier Name,Purchase No"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setSearch("");
                }}
              />
              {/* <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" /> */}
            </div>
          </Col>

        </Row>
      </div>

      {/* Purchase Transactions Table */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <Table bordered responsive className="table-striped customer-table">
            <thead className="table-dark">
              <tr>
                <th width="5%">S.No</th>
                <th width="10%">Purchase Date</th>
                <th width="12%">Purchase No</th>
                <th width="18%">Supplier Name</th>
                {/* <th width="15%">Supplier GSTIN</th> */}
                <th width="10%" className="text-end">Total GST</th>
                <th width="12%" className="text-end">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {currentTransactions.map((transaction, index) => (
                <tr key={transaction.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>
                    <strong>{transaction.date}</strong>
                  </td>
                  <td>
                    <Badge bg="light" text="dark">{transaction.bill_no}</Badge>
                  </td>
                  <td>
                    <div>
                      <div>{transaction.supplier_name}</div>
                    </div>
                  </td>
                  {/* <td>
                    <small>{transaction.supplierGSTIN}</small>
                  </td> */}
                  <td className="text-end fw-bold">₹{transaction.gst_amount}</td>
                  <td className="text-end fw-bold text-success">
                    ₹{transaction.grand_total}
                  </td>
                </tr>
              ))}
              {currentTransactions.length === 0 && (
                <tr>
                  <td colSpan="14" className="text-center py-4">
                    <div className="text-muted">
                      <FaFileInvoice size={48} className="mb-3" />
                      <p>No purchase transactions found</p>
                      <small>Try adjusting your search or filters</small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            {/* Summary Footer Row */}
            {filteredTransactions.length > 0 && (
              <tfoot className="table-warning">
                <tr>
                  <td colSpan="4" className="text-end fw-bold">Totals:</td>
                  <td className="text-end fw-bold">
                    ₹{formatAmount(totals.gst_amount)}
                  </td>
                  <td className="text-end fw-bold text-success">
                    ₹{formatAmount(totals.grand_total)}
                  </td>
                </tr>

              </tfoot>
            )}
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

    </Container>
  );
};

export default PurchaseGSTReport;